import type * as THREE from 'three';
import { STAGE, type GrassBlock } from '@vrlearning/lesson-schema';
import { marram } from '../coast.js';
import { bladeAtlas, grassTufts, type Tuft } from '../grass.js';
import type { WorldBuilder } from '../world.js';

function inside(points: readonly (readonly [number, number])[], x: number, z: number): boolean {
  let hit = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, zi] = points[i]!;
    const [xj, zj] = points[j]!;
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) hit = !hit;
  }
  return hit;
}

/** Where a grass block's tufts stand: clumps placed over its area's box, kept where their centre is inside it, high enough, off the stage. */
export function grassBlockTufts(
  block: GrassBlock,
  ground: (x: number, z: number) => number,
): Tuft[] {
  const xs = block.area.map((p) => p[0]);
  const zs = block.area.map((p) => p[1]);
  const [x0, x1, z0, z1] = [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)];
  // A clump stands where its centre is inside the area; its stalks spread round it, by at most
  // `spread / 2` over the area's edge.
  let centred = false;
  return marram(block.seed, {
    height: ground,
    clumps: block.clumps,
    place: (next) => {
      const x = x0 + next() * (x1 - x0);
      const z = z0 + next() * (z1 - z0);
      centred = inside(block.area, x, z);
      return [x, z];
    },
    keep: (x, z, y) => centred && y >= block.above && Math.hypot(x, z) >= STAGE.clear + 0.5,
    tuft: {
      perClump: block.perClump,
      spread: block.spread,
      height: block.height,
      width: block.width,
      shade: block.shade,
    },
  });
}

/** The grass block (W2; from grass.ts and the beach's marram): one instanced mesh, swaying in the world's wind. */
export function buildGrassBlock(
  world: WorldBuilder,
  block: GrassBlock,
  env: { ground: (x: number, z: number) => number; name: string },
): THREE.InstancedMesh {
  const mesh = grassTufts(grassBlockTufts(block, env.ground), world.wind, {
    map: world.codeTexture(bladeAtlas(128, 17, { root: block.root, tip: block.tip })),
    name: env.name,
    sway: block.sway,
  });
  world.addDressing(mesh);
  return mesh;
}
